import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collapseStrategies } from '../../utils/optionStrategies';
import { useAuth } from '../../contexts/AuthContext';
import { TradeTagCell, useTradeTags } from '../insights/TradeTags';
import { fetchClientPositions, fetchClientTrades } from '../../services/vectorDashboardApi';
import { BROKER_BRAND, resolveBrokerKey } from './brokerBranding';
import { IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight } from '../Icons';
import AccountSmartTiles from './AccountSmartTiles';
import AccountPeriodStats from './AccountPeriodStats';
import PositionReconciliationSummary, { OpenPositionsDetailRows, positionsSyncNote } from './PositionReconciliation';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const PAGE_SIZE = 25;

function monthKeyOf(dateStr) {
  return String(dateStr || '').slice(0, 7); // YYYY-MM
}

function formatMonthShort(monthKey) {
  const [y, m] = monthKey.split('-');
  return `${MONTH_LABELS[Number(m) - 1]} ${y}`;
}

function formatMonthFull(monthKey) {
  const [y, m] = monthKey.split('-');
  return `${MONTH_FULL[Number(m) - 1]} ${y}`;
}

function formatTradeDate(dateStr) {
  const [y, m, d] = String(dateStr || '').split('-');
  if (!y || !m || !d) return dateStr || '—';
  return `${m}/${d}/${y}`;
}

function formatMoney(value, { signed = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (signed) return `${num < 0 ? '-' : '+'}$${abs}`;
  return `${num < 0 ? '-' : ''}$${abs}`;
}

function formatRoi(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  return `${num.toFixed(2)}%`;
}

function pnlClass(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  return Number(value) >= 0 ? 'vd-positive' : 'vd-negative';
}

function AccountBadge({ client, size }) {
  const key = resolveBrokerKey(client.name, client.broker);
  const brand = key ? BROKER_BRAND[key] : null;
  const sizeClass = size === 'sm' ? ' sm' : '';
  if (brand?.logoSrc) {
    return (
      <span className={`vd-broker-logo ${brand.tone} has-image${sizeClass}`} aria-hidden="true">
        <img src={brand.logoSrc} alt="" />
      </span>
    );
  }
  return (
    <span className={`vd-broker-logo${sizeClass}`} aria-hidden="true">
      {brand?.shortName || (client.broker || '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

/** Single-account dropdown with broker logos, styled like the growth-chart picker. */
function TradeHistoryAccountSelect({ clients, selectedId, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = clients.find((c) => c.id === selectedId) || null;

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    }
    function handleEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className={`vd-select vd-trade-history-select${open ? ' open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="vd-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={clients.length === 0}
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected ? (
          <span className="vd-trade-history-select-value">
            <AccountBadge client={selected} size="sm" />
            <span className="vd-select-value">{selected.name}</span>
          </span>
        ) : (
          <span className="vd-select-placeholder">Select account</span>
        )}
        <IconChevronDown size={16} className="vd-select-chevron" />
      </button>

      {open && clients.length > 0 && (
        <div className="vd-select-menu vd-account-picker-menu" role="listbox">
          {clients.map((client) => {
            const active = client.id === selectedId;
            return (
              <button
                key={client.id}
                type="button"
                role="option"
                aria-selected={active}
                className={`vd-account-picker-row${active ? ' active' : ''}`}
                onClick={() => {
                  onChange(client.id);
                  setOpen(false);
                }}
              >
                <AccountBadge client={client} size="sm" />
                <span>{client.name}</span>
                {active && <IconCheck size={14} className="vd-select-option-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TradeHistory({
  clients,
  defaultClientId = null,
  dateRange = 'All',
  compact = false,
  refreshKey = 0,
}) {
  // Override is a temporary local choice; null means "follow the graph selection".
  const [overrideId, setOverrideId] = useState(null);
  const [trades, setTrades] = useState([]);
  const [positionsPayload, setPositionsPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [monthIndex, setMonthIndex] = useState(0);
  const [page, setPage] = useState(0);

  // When the graph dropdown selection changes, snap back to it.
  useEffect(() => {
    setOverrideId(null);
  }, [defaultClientId]);

  const selectedId = useMemo(() => {
    const candidate = overrideId ?? defaultClientId;
    if (candidate && clients.some((c) => c.id === candidate)) return candidate;
    return clients[0]?.id || null;
  }, [overrideId, defaultClientId, clients]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedId) || null,
    [clients, selectedId],
  );

  // Tags and notes are the trader's own: only on their own accounts.
  const { user } = useAuth() || {};
  const ownAccount = Boolean(selectedClient) && (
    selectedClient.targetUserId == null || user?.id == null || String(selectedClient.targetUserId) === String(user.id)
  );
  const tradeTags = useTradeTags(ownAccount);

  useEffect(() => {
    let cancelled = false;

    async function loadTrades() {
      if (!selectedClient) {
        setTrades([]);
        setPositionsPayload(null);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await fetchClientTrades(selectedClient.dataSeriesName, 'All');
        if (!cancelled) {
          // Option legs placed together (a call spread) show as one trade.
          setTrades(collapseStrategies(res.trades || []));
          setMonthIndex(0);
          setPage(0);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e.message);
          setTrades([]);
          setPositionsPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTrades();
    return () => { cancelled = true; };
  }, [selectedClient, refreshKey]);

  const months = useMemo(() => {
    const keys = new Set();
    for (const t of trades) {
      const key = monthKeyOf(t.exitDate || t.entryDate);
      if (key.length === 7) keys.add(key);
    }
    return Array.from(keys).sort((a, b) => b.localeCompare(a));
  }, [trades]);

  const activeMonth = months[monthIndex] || null;

  useEffect(() => {
    let cancelled = false;

    async function loadPositions() {
      if (!selectedClient?.dataSeriesName) {
        setPositionsPayload(null);
        return;
      }
      try {
        const res = await fetchClientPositions(selectedClient.dataSeriesName, {
          monthKey: activeMonth,
        });
        if (!cancelled) setPositionsPayload(res);
      } catch (e) {
        if (!cancelled) {
          console.error('[trade-history-positions]', e);
          setPositionsPayload(null);
        }
      }
    }

    loadPositions();
    return () => { cancelled = true; };
  }, [selectedClient, activeMonth, refreshKey]);

  const monthTrades = useMemo(() => {
    if (!activeMonth) return [];
    return trades.filter((t) => monthKeyOf(t.exitDate || t.entryDate) === activeMonth);
  }, [trades, activeMonth]);

  const pageCount = Math.max(1, Math.ceil(monthTrades.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageTrades = monthTrades.slice(pageStart, pageStart + PAGE_SIZE);

  function changeMonth(nextIndex) {
    setMonthIndex(nextIndex);
    setPage(0);
  }

  const openPositions = positionsPayload?.supportsOpenPositions
    ? (positionsPayload.positions || [])
    : [];
  const reconciliation = positionsPayload?.reconciliation || null;
  const showPositionsBlock = Boolean(positionsPayload?.supportsOpenPositions && reconciliation);
  const showTradeTable = monthTrades.length > 0 || openPositions.length > 0 || showPositionsBlock;

  if (!clients.length) return null;

  const tradesSection = (
    <>
      {!compact && (
        <div className="vd-trade-history-header">
          <div>
            <span className="vd-panel-eyebrow">Portfolio Tracker (Broker Sync)</span>
            <h3 className="vd-client-growth-title">Account Details</h3>
            <p className="vd-client-growth-subtitle">
              Performance metrics and closed trades for each connected account.
            </p>
          </div>
          <TradeHistoryAccountSelect
            clients={clients}
            selectedId={selectedId}
            onChange={setOverrideId}
          />
        </div>
      )}

      <div className="vd-trade-history-main">
        {!compact && selectedClient && (
          <div className="vd-trade-history-account-title">
            <AccountBadge client={selectedClient} />
            <div>
              <strong>{selectedClient.broker}</strong>
              <small>{selectedClient.name}</small>
            </div>
          </div>
        )}

        {!compact && selectedClient && (
          <AccountSmartTiles
            dataSeriesName={selectedClient.dataSeriesName}
            dateRange={dateRange}
          />
        )}

        {!compact && selectedClient && (
          <AccountPeriodStats dataSeriesName={selectedClient.dataSeriesName} />
        )}

        <div className="vd-trade-history-toolbar">
          <h4 className="vd-trade-history-month-title">
            {activeMonth ? `Trades for ${formatMonthFull(activeMonth)}` : 'Trades'}
          </h4>
            {months.length > 0 && (
              <div className="vd-trade-history-month-nav" role="group" aria-label="Month navigation">
                <button
                  type="button"
                  className="vd-trade-history-month-btn"
                  disabled={monthIndex >= months.length - 1}
                  onClick={() => changeMonth(Math.min(monthIndex + 1, months.length - 1))}
                  aria-label="Previous month"
                >
                  <IconChevronLeft size={14} />
                </button>
                <span className="vd-trade-history-month-label">
                  {activeMonth ? formatMonthShort(activeMonth) : '—'}
                </span>
                <button
                  type="button"
                  className="vd-trade-history-month-btn"
                  disabled={monthIndex <= 0}
                  onClick={() => changeMonth(Math.max(monthIndex - 1, 0))}
                  aria-label="Next month"
                >
                  <IconChevronRight size={14} />
                </button>
              </div>
            )}
          </div>

          {error && <p className="vd-error">{error}</p>}

          {loading ? (
            <div className="vd-trade-history-empty">Loading trades…</div>
          ) : !showTradeTable ? (
            <div className="vd-trade-history-empty">
              {trades.length === 0
                ? `No closed trades synced yet for ${selectedClient?.broker || 'this account'}.`
                : 'No trades in this month.'}
            </div>
          ) : (
            <>
              {monthTrades.length === 0 && openPositions.length > 0 ? (
                <p className="vd-trade-history-empty vd-trade-history-empty-inline">
                  No closed trades in this month. Open positions are shown below.
                </p>
              ) : null}

              <div className="vd-table-wrap">
                <table className="vd-table vd-trade-history-table vd-trade-history-table-with-positions">
                  <thead>
                    <tr>
                      <th>Trade Date</th>
                      <th>Symbol</th>
                      <th>Side</th>
                      <th className="vd-num">Qty</th>
                      <th className="vd-num">Entry Price</th>
                      <th className="vd-num">Exit / Current</th>
                      <th className="vd-num">Profit</th>
                      <th className="vd-num" title="Realized only — not account balance">Cumulative</th>
                      <th className="vd-num">ROI</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageTrades.map((trade, i) => (
                      <tr key={`${trade.exitDate}-${trade.symbol}-${pageStart + i}`}>
                        <td>{formatTradeDate(trade.exitDate || trade.entryDate)}</td>
                        <td>
                          {trade.symbol || '—'}
                          {trade.legsText ? <small className="vd-strategy-legs">{trade.legsText}</small> : null}
                          {tradeTags.enabled && ownAccount ? (
                            <TradeTagCell trade={trade} account={selectedClient?.name} tags={tradeTags.tags} onSave={tradeTags.save} />
                          ) : null}
                        </td>
                        <td>{trade.sideLabel || (trade.side ? trade.side.toUpperCase() : '—')}</td>
                        <td className="vd-num">{trade.quantity ?? '—'}</td>
                        <td className="vd-num">{formatMoney(trade.entryPrice)}</td>
                        <td className="vd-num">{formatMoney(trade.exitPrice)}</td>
                        <td className={`vd-num ${pnlClass(trade.profit)}`}>{formatMoney(trade.profit, { signed: true })}</td>
                        <td className={`vd-num ${pnlClass(trade.cumulativeProfit)}`}>{formatMoney(trade.cumulativeProfit, { signed: true })}</td>
                        <td className={`vd-num ${pnlClass(trade.roi)}`}>{formatRoi(trade.roi)}</td>
                        <td><span className="vd-position-status vd-position-status-closed">Closed</span></td>
                      </tr>
                    ))}
                    <OpenPositionsDetailRows
                      positions={openPositions}
                      asOf={positionsPayload?.asOf}
                    />
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && monthTrades.length > 0 ? (
                <div className="vd-trade-history-pagination">
                  <span className="vd-trade-history-page-info">
                    Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, monthTrades.length)} of {monthTrades.length} trades
                  </span>
                  <div className="vd-trade-history-month-nav" role="group" aria-label="Page navigation">
                    <button
                      type="button"
                      className="vd-trade-history-month-btn"
                      disabled={safePage <= 0}
                      onClick={() => setPage(safePage - 1)}
                      aria-label="Previous page"
                    >
                      <IconChevronLeft size={14} />
                    </button>
                    <span className="vd-trade-history-month-label">
                      Page {safePage + 1} of {pageCount}
                    </span>
                    <button
                      type="button"
                      className="vd-trade-history-month-btn"
                      disabled={safePage >= pageCount - 1}
                      onClick={() => setPage(safePage + 1)}
                      aria-label="Next page"
                    >
                      <IconChevronRight size={14} />
                    </button>
                  </div>
                </div>
              ) : null}

              {showPositionsBlock ? (
                <>
                  {!openPositions.length && positionsPayload?.supportsPerSymbolPositions !== false ? (
                    <p className="vd-open-positions-empty-note vd-trade-history-empty-inline">
                      {positionsSyncNote(
                        positionsPayload?.broker,
                        positionsPayload?.supportsPerSymbolPositions,
                      )}
                    </p>
                  ) : null}
                  <PositionReconciliationSummary
                    reconciliation={reconciliation}
                    monthKey={activeMonth}
                    monthLabelText={activeMonth ? formatMonthFull(activeMonth) : ''}
                    className="vd-reconciliation-detail"
                    broker={positionsPayload?.broker}
                    supportsPerSymbolPositions={positionsPayload?.supportsPerSymbolPositions}
                  />
                </>
              ) : null}
            </>
          )}
      </div>
    </>
  );

  if (compact) {
    return (
      <section className="vd-client-chart-section">
        <div className="vd-panel vd-client-report-panel vd-trade-history-panel vd-trade-history-panel-compact">
          {tradesSection}
        </div>
      </section>
    );
  }

  return (
    <section className="vd-client-chart-section">
      <div className="vd-panel vd-client-report-panel vd-trade-history-panel">
        {tradesSection}
      </div>
    </section>
  );
}
